import React, { useEffect, useMemo, useRef, useState } from "react";
import { LinearProgress } from "@mui/material";
import { notification, Pagination } from "antd";
import { useDispatch } from "react-redux";
import Select, { components } from "react-select";

import { getSignHistory } from "../../../../../actions/v2/JobSwmsTaskActions";
import { Strings } from "../../../../../dataProvider/localize";
import { signHistoryV2StaffSelectStyle } from "./SignHistoryV2SelectStyle";
import SignHistoryV2Row from "./SignHistoryV2Row";
import styles from "./SignHistoryV2.module.css";

const PAGE_SIZE = 10;

export const hasSigningGapForSignHistory = (sign) => {
  const rowStaffs = sign && Array.isArray(sign.staffs) ? sign.staffs : [];
  const signedStaffCount = rowStaffs.filter((staff) => staff && staff.staff_sign).length;
  const totalStaffCount = rowStaffs.length;

  return signedStaffCount !== totalStaffCount;
};

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <components.DownChevron />
    </components.DropdownIndicator>
  );
};

export default function SignHistoryV2({
  jobId,
  onOpenPreStart,
  onOpenSwms,
  onOpenEmergencyPlans,
  onOpenEmergencyProcedures,
  onOpenRescueReadiness,
  onOpenWhsPack
}) {
  const dispatch = useDispatch();
  const controller = useRef();

  const [loading, setLoading] = useState(false);
  const [allSigns, setAllSigns] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [page, setPage] = useState(1);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [showGapsOnly, setShowGapsOnly] = useState(false);

  const fetchSignHistory = () => {
    if (!jobId) {
      setAllSigns([]);
      setStaffs([]);
      return;
    }

    setLoading(true);

    if (controller.current) {
      controller.current.abort();
    }

    controller.current = new AbortController();

    dispatch(getSignHistory({ job_id: jobId }, { signal: controller.current.signal }))
      .then((res) => {
        setAllSigns(res && Array.isArray(res.swmsFormSignData) ? res.swmsFormSignData : []);
        setStaffs(res && Array.isArray(res.swmsFormSignUser) ? res.swmsFormSignUser : []);
        setLoading(false);
      })
      .catch((err) => {
        if (err && err.name === "AbortError") {
          return;
        }

        setLoading(false);
        notification.error({
          message: Strings.error_title,
          description: "There was an error while fetching Swms Sign History.",
          className: "ant-error"
        });
      });
  };

  useEffect(() => {
    fetchSignHistory();

    return () => {
      if (controller.current) {
        controller.current.abort();
      }
    };
  }, [jobId]);

  const filteredSigns = useMemo(() => {
    return allSigns.filter((sign) => {
      const rowStaffs = sign && Array.isArray(sign.staffs) ? sign.staffs : [];

      if (selectedStaff) {
        const hasSelectedStaff = rowStaffs.some((staff) => staff.user_name === selectedStaff);
        if (!hasSelectedStaff) {
          return false;
        }
      }

      if (showGapsOnly) {
        if (!hasSigningGapForSignHistory(sign)) {
          return false;
        }
      }

      return true;
    });
  }, [allSigns, selectedStaff, showGapsOnly]);

  useEffect(() => {
    setPage(1);
  }, [selectedStaff, showGapsOnly, allSigns]);

  const pagedSigns = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredSigns.slice(start, start + PAGE_SIZE);
  }, [filteredSigns, page]);

  const staffOptions = useMemo(() => {
    return staffs.map((staff) => ({
      user_name: staff.user_name,
      staff_name: staff.staff_name,
      label: staff.staff_name,
      value: staff.user_name
    }));
  }, [staffs]);

  const selectedStaffOption = useMemo(() => {
    if (!selectedStaff) {
      return null;
    }

    const matchedOption = staffOptions.find((option) => option.value === selectedStaff);
    return matchedOption || null;
  }, [selectedStaff, staffOptions]);

  const updateWhsPack = (sign, whsPack) => {
    setAllSigns((current) => current.map((currentSign) => (
      currentSign.task_id === sign.task_id
      && currentSign.swms_id === sign.swms_id
      && currentSign.shift_id === sign.shift_id
        ? {
          ...currentSign,
          whs_pack: {
            ...(currentSign.whs_pack || {}),
            can_download: true,
            can_generate: false,
            whs_pack: whsPack
          }
        }
        : currentSign
    )));
  };

  return (
    <div className={styles.container}>
      <div className="container-fluid mt-3 px-0">
        <div className={styles.headerRow}>
          <div className={styles.title}>Sign History</div>

          <div className={styles.controls}>
            <button
              type="button"
              className={`${styles.gapToggle} ${showGapsOnly ? styles.gapToggleActive : ""}`}
              onClick={() => setShowGapsOnly((prev) => !prev)}
            >
              Show gaps only
            </button>

            <div className={styles.staffSelectWrap}>
              <Select
                value={selectedStaffOption}
                placeholder="Select Staff"
                options={staffOptions}
                styles={signHistoryV2StaffSelectStyle}
                isClearable
                onChange={(option) => setSelectedStaff(option ? option.value : "")}
                components={{ DropdownIndicator }}
              />
            </div>
          </div>
        </div>

        <div className={styles.progressSlot}>{loading ? <LinearProgress /> : null}</div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead className={styles.tableHead}>
              <tr>
                <th style={{ width: "92px" }}>Date</th>
                <th style={{ width: "88px" }}>Shift Id</th>
                <th style={{ width: "180px" }}>Task Name</th>
                <th style={{ width: "170px" }}>SWMS Id</th>
                <th className={styles.docsColHead} style={{ width: "200px", textAlign: "center" }}>Documents</th>
                <th style={{ width: "72px" }}>Sign</th>
                <th>Crew</th>
              </tr>
            </thead>

            <tbody className={styles.tableBody}>
              {pagedSigns.map((sign) => {
                return (
                  <SignHistoryV2Row
                    key={`${sign.task_id}_${sign.swms_id}_${sign.shift_id}${sign.swms_number ? `_${sign.swms_number}` : ""}`}
                    sign={sign}
                    selectedStaff={selectedStaff}
                    jobId={jobId}
                    onOpenPreStart={onOpenPreStart}
                    onOpenSwms={onOpenSwms}
                    onOpenEmergencyPlans={onOpenEmergencyPlans}
                    onOpenEmergencyProcedures={onOpenEmergencyProcedures}
                    onOpenRescueReadiness={onOpenRescueReadiness}
                    onOpenWhsPack={onOpenWhsPack}
                    onWhsPackGenerated={updateWhsPack}
                  />
                );
              })}

              {!loading && pagedSigns.length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.emptyText}>No Sign History Available.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className={styles.footerRow}>
          <div className={styles.legend}>
            <span className={styles.legendItem}><span className={`${styles.legendDot} ${styles.legendDotSigned}`}></span>Signed</span>
            <span className={styles.legendItem}><span className={`${styles.legendDot} ${styles.legendDotNotSigned}`}></span>Not Signed</span>
            <span className={styles.legendItem}><span className={`${styles.legendDot} ${styles.legendDotDeclined}`}></span>Declined Shift</span>
          </div>
          <Pagination
            className="cs-pagination-v2"
            total={filteredSigns.length}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} Histories`}
            pageSize={PAGE_SIZE}
            current={page}
            onChange={(pageNo) => setPage(pageNo)}
            defaultCurrent={1}
          />
        </div>
      </div>
    </div>
  );
}
